import { useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import { useParams, Link, useHistory } from 'react-router-dom'
import apiGet from 'lib/network/apiGet'
import apiPost, { crmApiCreateLeadMagicLink } from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import { useTranslation } from 'react-i18next'
import useModal from 'hooks/useModal'
import LeadStageBadge from './components/LeadStageBadge'
import LeadContactActionModal from './components/LeadContactActionModal'
import LeadMagicLinkFieldPickerModal from './components/LeadMagicLinkFieldPickerModal'

const formatHours = timings => {
  const timetable = timings?.[0]?.timetable || []
  return timetable.length ? timetable.map(d => `${d.day.slice(0, 3)} ${d.active ? `${d.startTime.slice(0, 5)}–${d.endTime.slice(0, 5)}` : '✕'}`).join(', ') : '-'
}

const LeadProfile = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const history = useHistory()
  const [lead, setLead] = useState(null)
  const [isCreatingCompany, setIsCreatingCompany] = useState(false)
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false)
  const [latestMagicLink, setLatestMagicLink] = useState('')

  const fetchLead = async () => {
    const res = await apiGet(`crm/leads/${id}`)

    if (!res?.success || !res?.data) {
      history.push('/crm/leads')
      return
    }

    setLead(res.data)
    setLatestMagicLink(res.data.latestMagicLink || '')
  }

  useEffect(() => {
    fetchLead()
  }, [id])

  const handleCreateCompany = async () => {
    setIsCreatingCompany(true)
    try {
      const res = await apiPost(`crm/leads/${id}/create-company`)
      if (res?.success && res?.data?.companyId) {
        notify.success(t('crm.messages.companyCreatedFromLead'))
        history.push(`/company/edit/${res.data.companyId}/company-profile`)
        return
      }

      notify.error(res?.message || t('crm.messages.companyCreateFailed'))
    } catch (error) {
      notify.error(t('crm.messages.companyCreateFailed'))
    } finally {
      setIsCreatingCompany(false)
    }
  }

  const handleSendMagicLink = async payload => {
    setIsSendingMagicLink(true)
    try {
      const res = await crmApiCreateLeadMagicLink(id, payload)
      if (res?.success) {
        setLatestMagicLink(res?.data?.magicLink || '')
        notify.success('Magic link sent', 'Document upload link was sent to lead email')
        return
      }
      notify.error(res?.message || 'Failed to send magic link')
    } catch (error) {
      notify.error('Failed to send magic link')
    } finally {
      setIsSendingMagicLink(false)
    }
  }

  const [MagicLinkModal, openMagicLinkModal] = useModal({
    content: <LeadMagicLinkFieldPickerModal loading={isSendingMagicLink} onSubmit={handleSendMagicLink} />,
    title: 'Create Scoped Magic Link',
  })

  const [ContactModal, openContactModal] = useModal({
    content: <LeadContactActionModal leadId={id} t={t} onSuccess={fetchLead} />,
    title: t('crm.actions.addContact'),
  })

  if (!lead) return <Layout title={t('crm.title')} />

  const detailKeys = [
    'officeName',
    'commercialRegisterName',
    'commercialRegisterNumber',
    'ownerName',
    'email',
    'ownerIdNumber',
    'supervisingEmployeeName',
    'phoneNumber',
    'postbox',
    'postcode',
    'country',
    'governorate',
    'state',
    'area',
    'minimumPriceAllowedForPromotion',
    'tenantLowestAge',
    'tenantHighestAge',
    'deliveryPrice',
    'subscriptionType',
    'leadSource',
  ]

  const documents = [
    { key: 'ownerIdCardUrl', label: t('crm.documents.ownerIdCard') },
    { key: 'logoUrl', label: t('crm.documents.logo') },
    { key: 'commercialRegisterUrl', label: t('crm.documents.commercialRegister') },
    { key: 'carRentalAgreementUrl', label: t('crm.documents.carRentalAgreement') },
  ]

  return (
    <Layout
      title={lead.officeName}
      headerRight={
        <div className="mt-4 flex sm:mt-0 sm:ml-4 gap-3">
          <Link
            to="/crm/leads"
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white hover:text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-1 sm:ml-3"
          >
            {t('addEditPartner.buttons.goBack')}
          </Link>
        </div>
      }
    >
      <div className="p-4 space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="space-y-2">
              <LeadStageBadge stage={lead.stage} t={t} />
              <p className="text-xs text-gray-500">
                {t('crm.columns.createdAt')}: {new Date(lead.createdAt).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/crm/leads/edit/${lead._id}`}>
                <button type="button" className="h-[30px] inline-flex items-center px-4 py-1 rounded-md text-xs text-white bg-dark-600 hover:bg-dark-700">
                  {t('crm.actions.edit')}
                </button>
              </Link>
              <button type="button" className="h-[30px] inline-flex items-center px-4 py-1 rounded-md text-xs text-white bg-dark-600 hover:bg-dark-700" onClick={openContactModal}>
                {t('crm.actions.addContact')}
              </button>
              <button
                type="button"
                className={`h-[30px] inline-flex items-center px-4 py-1 rounded-md text-xs text-white bg-dark-600 hover:bg-dark-700 ${
                  isSendingMagicLink ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={openMagicLinkModal}
                disabled={isSendingMagicLink}
              >
                {isSendingMagicLink ? 'Sending Link...' : 'Generate Access Link'}
              </button>
              <button
                type="button"
                className={`h-[30px] inline-flex items-center px-4 py-1 rounded-md text-xs text-white bg-dark-600 hover:bg-dark-700 ${
                  isCreatingCompany ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleCreateCompany}
                disabled={isCreatingCompany}
              >
                {isCreatingCompany ? t('crm.actions.creatingCompany') : t('crm.actions.createCompany')}
              </button>
            </div>
          </div>
        </div>

        {latestMagicLink ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Latest Magic Link</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="text" readOnly value={latestMagicLink} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 bg-gray-50" />
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm text-white bg-dark-600 hover:bg-dark-700"
                onClick={() => {
                  navigator.clipboard.writeText(latestMagicLink)
                  notify.success('Copied', 'Magic link copied to clipboard')
                }}
              >
                Copy Access Link
              </button>
            </div>
          </div>
        ) : null}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">{t('crm.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {detailKeys.map(key => (
              <div key={key} className="text-sm">
                <span className="font-semibold text-gray-800">{t(`crm.labels.${key}`)}:</span>{' '}
                <span className="text-gray-700">{key === 'subscriptionType' ? t(`crm.packages.${lead[key]}`) || lead[key] : lead[key]}</span>
              </div>
            ))}
            <div className="text-sm">
              <span className="font-semibold text-gray-800">{t('crm.labels.registeredForVat')}:</span>{' '}
              <span className="text-gray-700">{lead.registeredForVat ? `${t('common.yes')}${lead.vatPercentage != null ? ` (${lead.vatPercentage}%)` : ''}` : t('common.no')}</span>
            </div>
          </div>
        </div>

        {lead.source === 'dashboard_signup' && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
            <p className="text-xs text-indigo-700">{t('crm.signupDetails.hasLogin')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              {[
                ['whatsapp', lead.whatsapp],
                ['phones', (lead.phones || []).map(p => `${p.title ? `${p.title}: ` : ''}${p.phone}`).join(', ')],
                ['depositAmount', lead.depositAmount],
              ].map(([key, value]) => (
                <div key={key} className="text-sm">
                  <span className="font-semibold text-gray-800">{t(`crm.signupDetails.${key}`)}:</span> <span className="text-gray-700">{value ?? '-'}</span>
                </div>
              ))}
            </div>
            {lead.officeInstructions?.length > 0 && (
              <div className="text-sm">
                <span className="font-semibold text-gray-800">{t('crm.signupDetails.officeInstructions')}:</span>
                <ul className="list-disc ms-5 text-gray-700">
                  {lead.officeInstructions.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-sm">
              <span className="font-semibold text-gray-800">{t('crm.signupDetails.branches')}:</span>
              {(lead.branches || []).map((branch, i) => (
                <div key={i} className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-gray-700">
                  <p className="font-medium text-gray-900">{branch.name}</p>
                  <p>
                    {branch.address}, {branch.governorate}
                    {branch.lat != null && branch.lon != null ? ` (${branch.lat}, ${branch.lon})` : ''}
                  </p>
                  {branch.phones?.length > 0 && <p>{branch.phones.map(p => p.phone).join(', ')}</p>}
                  {branch.isDeliveryAvailable && (
                    <p>
                      {t('companySignup.fields.deliveryKmRange')}: {branch.deliveryKmRange ?? '-'} ·{' '}
                      {(branch.deliveryFeeRules || []).map(rule => `${rule.fromKm}–${rule.toKm} km: ${rule.price}`).join(', ') ||
                        `${t('companySignup.fields.deliveryCost')}: ${branch.deliveryCost ?? '-'}`}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">{formatHours(branch.timings)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('crm.documents.title')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {documents.map(doc => (
              <a
                key={doc.key}
                className={`text-sm rounded-md px-3 py-2 border ${
                  lead[doc.key] ? 'text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 border-gray-200 bg-gray-50 pointer-events-none'
                }`}
                href={lead[doc.key] || '#'}
                target="_blank"
                rel="noreferrer"
              >
                {doc.label}
              </a>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('crm.contact.title')}</h3>
          {(lead.contactActions || []).length === 0 ? (
            <p className="text-sm text-gray-500">{t('common.noData')}</p>
          ) : (
            (lead.contactActions || []).map((item, idx) => (
              <div key={idx} className="border border-gray-200 rounded-md p-3 mb-2 bg-gray-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{item.type}</span>
                  <span className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-800 mb-1">{item.note}</p>
                <p className="text-xs text-gray-500">{item.createdBy?.fullName || item.createdBy?.email}</p>
              </div>
            ))
          )}
        </div>
      </div>
      <ContactModal />
      <MagicLinkModal />
    </Layout>
  )
}

export default LeadProfile
